import { computed, ref } from "vue";
import { getMusics } from "@/request/api/music";
import feedback from "@/services/feedback";

const audioElement = ref(null);
const playlist = ref([]);
const currentIndex = ref(0);
const loading = ref(true);
const loadError = ref("");
const isPlaying = ref(false);
const hasPlaybackStarted = ref(false);
const isBuffering = ref(false);
const audioFailed = ref(false);
const audioErrorMessage = ref("");
const retryingAudio = ref(false);
const duration = ref(0);
const currentTime = ref(0);
const seekValue = ref(0);
const isSeeking = ref(false);
const resumeAfterSeek = ref(false);
const activeLyricIndex = ref(-1);
const mode = ref(window.localStorage.getItem("music-play-mode") || "repeat-all");
const volume = ref(Number(window.localStorage.getItem("music-volume") || 0.8));
const lastVolume = ref(volume.value || 0.8);

let requestSequence = 0;
let playbackSequence = 0;
let loadingPromise = null;

const currentSong = computed(() => playlist.value[currentIndex.value] || {});
const lyrics = computed(() => currentSong.value.parsedLyrics || []);
const displayTime = computed(() => (isSeeking.value ? seekValue.value : currentTime.value));
const progressPercent = computed(() => (duration.value ? Math.min(100, (seekValue.value / duration.value) * 100) : 0));
const trackNumber = computed(() => String(currentIndex.value + 1).padStart(2, "0"));
const muted = computed(() => volume.value === 0);
const hasActiveSong = computed(() => Boolean(currentSong.value?.fileUrl));
const statusText = computed(() => audioFailed.value ? "UNAVAILABLE" : isBuffering.value ? "BUFFERING" : isPlaying.value ? "NOW PLAYING" : "READY TO PLAY");
const modeLabel = computed(() => ({ "repeat-all": "列表循环", order: "顺序播放", "repeat-one": "单曲循环" }[mode.value]));

function bindAudio(element) {
  audioElement.value = element || null;
  if (!element) return;
  element.volume = volume.value;
  if (currentSong.value.fileUrl) element.src = currentSong.value.fileUrl;
}

async function loadSongs(force = false) {
  if (!force && playlist.value.length) return playlist.value;
  if (!force && loadingPromise) return loadingPromise;

  const sequence = ++requestSequence;
  loading.value = true;
  loadError.value = "";
  loadingPromise = (async () => {
    try {
      const response = await getMusics();
      if (sequence !== requestSequence) return playlist.value;
      if (response?.code !== 200 || !Array.isArray(response.data)) throw new Error(response?.message || "音乐数据格式异常");
      const shouldInitializeTrack = !hasActiveSong.value;
      playlist.value = response.data
        .filter((song) => song?.fileUrl)
        .map((song) => ({ ...song, parsedLyrics: parseLyrics(song.lyrics) }));
      if (playlist.value.length && shouldInitializeTrack) await selectTrack(0, false);
      return playlist.value;
    } catch (error) {
      if (sequence !== requestSequence) return playlist.value;
      loadError.value = error?.message || "音乐列表加载失败，请稍后重试。";
      feedback.error({ title: "播放室加载失败", message: loadError.value });
      return [];
    } finally {
      if (sequence === requestSequence) loading.value = false;
      loadingPromise = null;
    }
  })();
  return loadingPromise;
}

async function selectTrack(index, autoplay = true) {
  if (index < 0 || index >= playlist.value.length) return;
  const audio = audioElement.value;
  const sameTrack = index === currentIndex.value && audio?.currentSrc;
  if (sameTrack && !audioFailed.value) {
    if (autoplay) await togglePlayback();
    return;
  }

  audio?.pause();
  currentIndex.value = index;
  resetTrackState();
  if (!audio) return;
  // Set the source and invoke play in the user event, without waiting for a Vue render.
  audio.src = currentSong.value.fileUrl;
  audio.volume = volume.value;
  audio.load();
  if (autoplay) await safePlay();
}

function resetTrackState() {
  playbackSequence += 1;
  retryingAudio.value = false;
  isPlaying.value = false;
  resumeAfterSeek.value = false;
  duration.value = 0;
  currentTime.value = 0;
  seekValue.value = 0;
  isSeeking.value = false;
  activeLyricIndex.value = -1;
  audioFailed.value = false;
  audioErrorMessage.value = "";
  isBuffering.value = false;
}

async function safePlay() {
  const audio = audioElement.value;
  const sequence = playbackSequence;
  if (!audio) return;
  try {
    await audio.play();
  } catch (error) {
    // 切歌或重新加载取消了旧播放请求，不应显示为当前曲目的错误。
    if (sequence !== playbackSequence || audio !== audioElement.value || error?.name === "AbortError") return;
    isPlaying.value = false;
    isBuffering.value = false;
    if (error?.name === "NotAllowedError") {
      feedback.warning({ title: "请手动播放", message: "浏览器暂未允许播放音频，请点击播放按钮重试。" });
    } else if (!audioFailed.value) {
      handleAudioError();
    }
  }
}

async function retryCurrentTrack() {
  const audio = audioElement.value;
  if (!audio || !hasActiveSong.value || retryingAudio.value) return;
  audio.pause();
  resetTrackState();
  const sequence = playbackSequence;
  retryingAudio.value = true;
  isBuffering.value = true;
  try {
    // 重试只重新读取当前音源，不请求或修改歌单；进度从头开始。
    audio.load();
    await safePlay();
  } catch (error) {
    if (sequence === playbackSequence && !audioFailed.value) handleAudioError();
  } finally {
    if (sequence === playbackSequence) {
      retryingAudio.value = false;
      isBuffering.value = false;
    }
  }
}

async function togglePlayback() {
  const audio = audioElement.value;
  if (!audio || retryingAudio.value) return;
  if (audioFailed.value) {
    await retryCurrentTrack();
    return;
  }
  if (audio.paused) await safePlay();
  else audio.pause();
}

function handlePlaying() {
  audioErrorMessage.value = "";
  audioFailed.value = false;
  isPlaying.value = true;
  hasPlaybackStarted.value = true;
  isBuffering.value = false;
}

function handlePause() {
  isPlaying.value = false;
}

function handleMetadata(event) {
  const value = event.target.duration;
  if (Number.isFinite(value)) duration.value = value;
}

function handleTimeUpdate(event) {
  const time = event.target.currentTime || 0;
  currentTime.value = time;
  if (!isSeeking.value) seekValue.value = time;
  syncLyric(time);
}

function beginSeek() {
  const audio = audioElement.value;
  isSeeking.value = true;
  resumeAfterSeek.value = Boolean(audio && !audio.paused);
}

function previewSeek(event) {
  if (!isSeeking.value) {
    isSeeking.value = true;
    resumeAfterSeek.value = Boolean(audioElement.value && !audioElement.value.paused);
  }
  seekValue.value = Number(event.target.value);
}

async function commitSeek(event) {
  const audio = audioElement.value;
  if (!audio || !Number.isFinite(audio.duration)) {
    isSeeking.value = false;
    return;
  }
  const target = Math.min(Math.max(Number(event.target.value), 0), audio.duration);
  audio.currentTime = target;
  currentTime.value = target;
  seekValue.value = target;
  isSeeking.value = false;
  syncLyric(target);
  if (resumeAfterSeek.value && audio.paused) await safePlay();
  resumeAfterSeek.value = false;
}

async function seekFromLyric(time) {
  beginSeek();
  seekValue.value = time;
  await commitSeek({ target: { value: time } });
}

function handleSeeked() {
  isBuffering.value = false;
}

async function handleEnded() {
  if (mode.value === "repeat-one") {
    audioElement.value.currentTime = 0;
    await safePlay();
    return;
  }
  if (currentIndex.value < playlist.value.length - 1) {
    await selectTrack(currentIndex.value + 1, true);
    return;
  }
  if (mode.value === "repeat-all") await selectTrack(0, true);
}

function nextTrack() {
  if (!playlist.value.length) return;
  selectTrack(currentIndex.value === playlist.value.length - 1 ? 0 : currentIndex.value + 1, true);
}

function previousTrack() {
  if (!playlist.value.length) return;
  if (audioElement.value?.currentTime > 4) {
    seekFromLyric(0);
    return;
  }
  selectTrack(currentIndex.value === 0 ? playlist.value.length - 1 : currentIndex.value - 1, true);
}

function cycleMode() {
  const modes = ["repeat-all", "order", "repeat-one"];
  mode.value = modes[(modes.indexOf(mode.value) + 1) % modes.length];
  window.localStorage.setItem("music-play-mode", mode.value);
  feedback.info(modeLabel.value);
}

function applyVolume() {
  if (!audioElement.value) return;
  audioElement.value.volume = volume.value;
  if (volume.value > 0) lastVolume.value = volume.value;
  window.localStorage.setItem("music-volume", String(volume.value));
}

function toggleMute() {
  volume.value = muted.value ? lastVolume.value : 0;
  applyVolume();
}

function handleAudioError() {
  const mediaError = audioElement.value?.error;
  // Aborting a previous load during a track change is not a failed new track.
  if (mediaError?.code === 1) return;
  const messages = {
    2: "音频读取中断，请检查网络后重新播放。",
    3: "浏览器无法解码这首歌。可以换一首，或使用 Safari 打开再试。",
    4: "音源暂不可用，或当前浏览器不支持该音频格式。请换一首或使用 Safari 打开。",
  };
  audioErrorMessage.value = messages[mediaError?.code] || "暂时无法播放这首歌，可以重新加载或换一首试试。";
  if (audioFailed.value) return;
  audioFailed.value = true;
  isPlaying.value = false;
  isBuffering.value = false;
  feedback.error({ title: "音频加载失败", message: audioErrorMessage.value });
}

function parseLyrics(rawLyrics) {
  if (!rawLyrics) return [];
  const result = [];
  String(rawLyrics).split(/\r?\n/).forEach((line) => {
    const text = line.replace(/\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g, "").trim();
    const matches = [...line.matchAll(/\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g)];
    matches.forEach((match) => {
      const fraction = match[3] ? Number(`0.${match[3]}`) : 0;
      if (text) result.push({ time: Number(match[1]) * 60 + Number(match[2]) + fraction, text });
    });
  });
  return result.sort((left, right) => left.time - right.time);
}

function syncLyric(time) {
  let index = -1;
  for (let i = lyrics.value.length - 1; i >= 0; i -= 1) {
    if (time >= lyrics.value[i].time) {
      index = i;
      break;
    }
  }
  activeLyricIndex.value = index;
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;
}

export function useMusicPlayer() {
  return {
    playlist,
    currentIndex,
    currentSong,
    lyrics,
    loading,
    loadError,
    isPlaying,
    hasPlaybackStarted,
    isBuffering,
    audioFailed,
    audioErrorMessage,
    retryingAudio,
    duration,
    currentTime,
    seekValue,
    isSeeking,
    activeLyricIndex,
    mode,
    volume,
    displayTime,
    progressPercent,
    trackNumber,
    muted,
    hasActiveSong,
    statusText,
    modeLabel,
    bindAudio,
    loadSongs,
    selectTrack,
    togglePlayback,
    retryCurrentTrack,
    beginSeek,
    previewSeek,
    commitSeek,
    seekFromLyric,
    nextTrack,
    previousTrack,
    cycleMode,
    applyVolume,
    toggleMute,
    handlePlaying,
    handlePause,
    handleMetadata,
    handleTimeUpdate,
    handleSeeked,
    handleEnded,
    handleAudioError,
    formatTime,
  };
}
